import {
  Control,
  useFieldArray,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import StyledFormSection from '../styled/StyledFormSection';
import StyledButton from '../styled/StyledButton';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import StyledTooltipContainer from '../styled/StyledTooltipContainer';
import StyledInput from '../styled/StyledInput';
import { Label } from '../ui/label';
import StyledRadioGroup from '../styled/StyledRadioGroup';
import TrashIcon from '@/assets/icons/trash-02.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { useTranslation } from '@/i18n';
import { SupportedLocale } from '@/types/supported-locale.type';
import StyledTranslatableInput from '../styled/StyledTranslatableInput';
import StyledTranslatableTextarea from '../styled/StyledTranslatableTextarea';
import { CarRentalFormDataWithTranslations } from '@/i18n/translation-type-utils';
import { useMemo } from 'react';
import { useTranslationFieldArray } from '@/hooks/useTranslationFieldArray';
import { useTranslateFields } from '@/hooks/useTranslate';
import TranslateIcon from '../translation/TranslateIcon';

interface OptionsListProps {
  control: Control<CarRentalFormDataWithTranslations>;
  language: SupportedLocale;
  isViewMode?: boolean;
}

const OptionsList = ({
  control,
  language,
  isViewMode = false,
}: OptionsListProps) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'options',
  });
  const { append: appendTranslation, remove: removeTranslation } =
    useTranslationFieldArray({
      control,
      name: 'options',
    });
  const { setValue } = useFormContext<CarRentalFormDataWithTranslations>();

  const { translateFields, isTranslating } = useTranslateFields({
    control: control,
    setValue,
    fields: [],
    nestedFields: {
      options: ['name', 'description'],
    },
    targetLanguage: language,
  });

  return (
    <StyledFormSection
      sectionTitle={t('carRental.categoryForm.addOption')}
      icon={
        <TranslateIcon
          translateFields={() => translateFields({ allNestedFields: true })}
          isTranslating={isTranslating}
          language={language}
          hidden={isViewMode}
        />
      }
    >
      {fields.map((item, index) => (
        <OptionsCard
          key={item.id}
          control={control}
          index={index}
          language={language}
          deleteOption={() => {
            remove(index);
            removeTranslation(index);
          }}
          isViewMode={isViewMode}
        />
      ))}
      {!isViewMode && (
        <StyledButton
          className="text-brand-600 w-fit"
          title={t('carRental.categoryForm.addOption')}
          onClick={() => {
            append({
              name: undefined,
              description: undefined,
              amount: undefined,
              billingType: undefined,
            } as CarRentalFormDataWithTranslations['options'][number]);
            appendTranslation(() => ({
              name: undefined,
              description: undefined,
            }));
          }}
          variant="outlined"
          icon={<PlusIcon className="size-5 fill-brand-600" />}
        />
      )}
    </StyledFormSection>
  );
};

export default OptionsList;

interface OptionsCardProps {
  control: Control<CarRentalFormDataWithTranslations>;
  index: number;
  language: SupportedLocale;
  deleteOption: () => void;
  isViewMode?: boolean;
}

const OptionsCard = ({
  control,
  index,
  language,
  deleteOption,
  isViewMode = false,
}: OptionsCardProps) => {
  const { t } = useTranslation();
  const currentTitle = useWatch({
    control,
    name: `options.${index}.name`,
  });
  const translations = useWatch({
    control,
    name: 'translations',
  });
  const { setValue } = useFormContext<CarRentalFormDataWithTranslations>();

  const cardTitle = useMemo(() => {
    const currentTranslation = translations?.[language]?.options?.[index];
    const currentTitleTranslated = currentTranslation?.name ?? currentTitle;
    return (
      currentTitleTranslated ||
      currentTitle ||
      t('carRental.categoryForm.option', { index: index + 1 })
    );
  }, [currentTitle, index, language, t, translations]);

  return (
    <Card className="w-full">
      <CardHeader className="flex flex-row justify-between">
        <CardTitle className="text-base leading-6 hover:cursor-pointer">
          {cardTitle}
        </CardTitle>
        <div className="flex gap-10">
          <div className="flex gap-4">
            {index > 0 && !isViewMode && (
              <StyledTooltipContainer
                tooltipMessage={t('carRental.categoryForm.delete')}
              >
                <TrashIcon
                  className="size-5 stroke-brand-600 hover:cursor-pointer"
                  onClick={deleteOption}
                />
              </StyledTooltipContainer>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="flex flex-col gap-6">
        <div className="flex flex-col gap-4">
          <StyledTranslatableInput
            originalName={`options.${index}.name`}
            control={control}
            title={t('carRental.categoryForm.name')}
            name={`options.${index}.name`}
            setValue={setValue}
            language={language}
            placeholder={t('carRental.categoryForm.namePlaceholder')}
            disabled={isViewMode}
          />
          <StyledTranslatableTextarea
            originalName={`options.${index}.description`}
            control={control}
            title={t('carRental.categoryForm.optionDescription')}
            name={`options.${index}.description`}
            setValue={setValue}
            language={language}
            placeholder={t(
              'carRental.categoryForm.optionDescriptionPlaceholder',
            )}
            rows={3}
            disabled={isViewMode}
          />
          <div className="flex flex-col gap-1">
            <Label className="text-gray-700">
              {t('carRental.categoryForm.amount')}
            </Label>
            <div className="flex gap-6 w-full items-center">
              <div className="min-w-20 max-w-40">
                <StyledInput
                  control={control}
                  name={`options.${index}.amount`}
                  placeholder={t('carRental.categoryForm.amountPlaceholder')}
                  type="number"
                  min={0}
                  disabled={isViewMode}
                />
              </div>
              <StyledRadioGroup
                control={control}
                name={`options.${index}.billingType`}
                options={[
                  {
                    label: t('carRental.categoryForm.billingTypePerDay'),
                    value: 'perDay',
                  },
                  {
                    label: t('carRental.categoryForm.billingTypePerRental'),
                    value: 'perRental',
                  },
                ]}
                className="w-1/2"
                disabled={isViewMode}
              />
            </div>
            <p className="text-sm text-gray-500">
              {t('carRental.globalFormDescription')}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
